import { useState } from 'react';
import { TextEditorView, type ViewerSourceEditing } from './TextEditorView';
import { ViewEditToggle } from './ViewEditToggle';

// Injected ahead of the document so previewed HTML cannot fetch external
// subresources: no scripts, no network, no forms — inline styles and data-URI
// images still render. A content-supplied CSP meta combines by intersection
// (safe); injecting before an existing <!DOCTYPE> puts the frame in quirks
// mode (cosmetic only, accepted).
const CSP_PREFIX =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src data:; style-src \'unsafe-inline\'">';

/**
 * Sandboxed HTML preview. The preview iframe carries an EMPTY sandbox token
 * list (no scripts, opaque origin) plus the CSP prefix above. With an editable
 * host, a View/Edit switch shows the CM6 editor instead; `content` is the
 * buffer at SYNC POINTS (the pane only re-renders it at debounce/save
 * boundaries, so the iframe's srcDoc never churns per keystroke), and dirty
 * pins the switch to Edit with a "save to preview" hint.
 */
export function HtmlView({
  content,
  filename,
  editing,
}: {
  content: string;
  filename: string;
  editing?: ViewerSourceEditing;
}) {
  const [localEditMode, setLocalEditMode] = useState(false);
  const editable = editing != null && !editing.readOnly;
  const hosted = editing?.editMode !== undefined;
  const editMode = editing?.editMode ?? localEditMode;
  const setEditMode = editing?.onEditModeChange ?? setLocalEditMode;
  // Dirty pins the switch to Edit — the preview would silently show
  // stale (or half-synced) markup while edits live in the editor.
  const pinnedToEdit = editing?.dirty === true;
  const showEditor = editable && (pinnedToEdit || editMode);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-md text-body">
      {editable && !hosted && (
        <div className="flex items-center justify-end gap-1 border-b px-3 py-1.5">
          {pinnedToEdit && (
            <span className="mr-auto text-caption text-muted-foreground" data-testid="save-to-preview-hint">
              Save to preview
            </span>
          )}
          <ViewEditToggle editing={showEditor} onChange={setEditMode} disabled={pinnedToEdit} />
        </div>
      )}
      {showEditor ? (
        <div className="min-h-0 flex-1 p-1">
          <TextEditorView
            content={content}
            filename={filename}
            readOnly={false}
            onDocChanged={editing.onDocChanged}
            getTextRef={editing.getTextRef}
            onSave={editing.onSave}
          />
        </div>
      ) : (
        <iframe
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={CSP_PREFIX + content}
          title={filename}
          className="min-h-0 w-full flex-1 border-0 bg-white"
        />
      )}
    </div>
  );
}
